/**
 * buildTicketTrainingContext
 * 
 * Aggregates workflow context from multiple ticket-related tables
 * for use in training artifact scoring and recommendation.
 * 
 * Sources (in order):
 * 1. ticket_workflow_complaints (text)
 * 2. ticket_workflow_notes (note_text)
 * 3. ticket_workflow_diagnostics (concern, conditions, findings, root_cause, recommendation, scan_details, dtc_list)
 * 4. ticket_workflow_inspection_items (label, notes) — filtered to non-null notes
 * 5. ticket_thread_events (content) — filtered to channel IN ('sms', 'email', 'voice')
 * 
 * Legacy fallback: If all 5 workflow sections return empty, queries ticket_jobs.notes.
 * 
 * Comms note: Thread events are the v1 comms corpus. Production currently only has
 * 'system' channel, so comms will be empty until SMS/email/voice events land.
 * The filter is forward-compatible. If/when a structured communications table is added,
 * it becomes the primary source.
 */

import { supabase } from '@/integrations/supabase/client';
import { extractDTCs } from '@/utils/contextualTrainingScorer';
// BAT-A1: bound `.in('ticket_id', ticketIds)` clause sizes for the 5+1
// sister reads in `buildTicketTrainingContextBatch`. A busy multi-tech
// schedule window (±5 days) feeds this function 500–2000 ticket IDs in a
// single recommendation refresh; without chunking each of those IDs lands
// in the IN(...) clause of every sister read at once, eventually
// degrading planner cost and PostgREST request payload size. Chunking at
// DEFAULT_CHUNK_SIZE (500) keeps each individual request comfortable
// while preserving the parallel-fan-out shape across the 5 reads.
import { chunkInQuery } from '@/utils/chunkInQuery';

// =============================================
// TYPES
// =============================================

/**
 * CORE-AH-INT-005 — the identity of a context source, so a failure can be
 * attributed to a named source rather than disappearing into an empty array.
 */
export type ContextSourceId =
  | 'complaints'
  | 'notes'
  | 'diagnostics'
  | 'inspection'
  | 'communications'
  | 'optibay'
  /**
   * The legacy `ticket_jobs.notes` fallback. It is attempted only for tickets
   * whose workflow sections produced nothing, so it appears in `sourceStates`
   * only on that path — "attempted" is the membership rule, not "exists".
   */
  | 'legacy_notes';

/**
 * THE THREE STATES A SOURCE CAN BE IN.
 *
 * Before CORE-AH-INT-005 there were two, because every read was consumed as
 * `res.data || []` — so "the query failed" and "this ticket genuinely has no
 * complaints" produced the identical empty array, and a recommendation built
 * on the second was indistinguishable from one built on the first.
 *
 * CORE-AH's constitution forbids exactly that: absent context SHALL produce an
 * explicit incomplete state, and UNKNOWN REMAINS UNKNOWN. A failed read is
 * unknown, not empty.
 */
export interface ContextSourceState {
  id: ContextSourceId;
  /** The read itself failed — RLS denial, timeout, network. UNKNOWN. */
  unavailable: boolean;
  /** Rows returned when the read SUCCEEDED. `null` when unavailable — not 0. */
  rowCount: number | null;
}

export interface TicketTrainingContext {
  /** Section-labeled concatenation of all workflow text */
  text: string;
  /** Deduplicated, validated DTC codes */
  dtcs: string[];
  /** Which sections contributed content — uses match_source naming */
  sources: string[];
  /**
   * CORE-AH-INT-005 — per-source read status. Present for every source that
   * was attempted, whether or not it contributed text.
   */
  sourceStates: ContextSourceState[];
  /** Sources whose read FAILED. Non-empty means the context is incomplete. */
  unavailableSources: ContextSourceId[];
  /** False when any attempted source failed to read. */
  contextComplete: boolean;
}

/**
 * ONE collector, not fourteen bespoke error flows.
 *
 * Supabase returns `{ data, error }`; this preserves which source a result
 * came from and whether it failed, and hands back rows the callers can use
 * exactly as before. A failed read yields no rows AND a recorded failure —
 * never silently-empty rows.
 */
export function collectContextSource<T>(
  id: ContextSourceId,
  res: { data: T[] | null; error: unknown | null } | null | undefined,
): { rows: T[]; state: ContextSourceState } {
  const failed = !!res?.error;
  const rows = failed ? [] : (res?.data ?? []);
  return {
    rows,
    state: { id, unavailable: failed, rowCount: failed ? null : rows.length },
  };
}

/** Derive the two convenience fields from the collected states. */
export function summariseContextStates(states: ContextSourceState[]): {
  sourceStates: ContextSourceState[];
  unavailableSources: ContextSourceId[];
  contextComplete: boolean;
} {
  const unavailableSources = states.filter(x => x.unavailable).map(x => x.id);
  return { sourceStates: states, unavailableSources, contextComplete: unavailableSources.length === 0 };
}

/** Core-100 R4 W4 (CORE-AF): how an AI-derived timeline entry is labelled inside the communications context. */
export const AI_DERIVED_COMMS_PREFIX = '(derived by AI)';

/** Canonical source names — matches suggestionLogger match_source values */
export type WorkflowSource =
  | 'workflow_complaints'
  | 'workflow_notes'
  | 'workflow_diagnostics'
  | 'workflow_inspection'
  | 'workflow_comms'
  | 'legacy_notes'
  | 'optibay_diagnostics';

// =============================================
// DTC PARSING
// =============================================

const DTC_REGEX = /^[PBCU][0-9]{4}$/;

/**
 * Parse DTCs from a dtc_list text column.
 * Handles JSON arrays, comma-separated, and whitespace-separated formats.
 */
function parseDtcList(raw: string | null | undefined): string[] {
  if (!raw || !raw.trim()) return [];

  let tokens: string[];

  // Try JSON array first
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      tokens = parsed.map(String);
    } else {
      tokens = raw.split(/[,\s]+/);
    }
  } catch {
    tokens = raw.split(/[,\s]+/);
  }

  return tokens
    .map(t => t.trim().toUpperCase())
    .filter(t => DTC_REGEX.test(t));
}

// =============================================
// TEXT ASSEMBLY HELPERS
// =============================================

interface DiagnosticRow {
  concern: string | null;
  conditions: string | null;
  findings: string | null;
  root_cause: string | null;
  recommendation: string | null;
  scan_details: string | null;
  dtc_list: string | null;
}

function assembleDiagnosticText(row: DiagnosticRow): string {
  const parts = [
    row.concern,
    row.conditions,
    row.findings,
    row.root_cause,
    row.recommendation,
    row.scan_details,
  ].filter(Boolean);
  return parts.join(' ');
}

// =============================================
// SINGLE-TICKET FUNCTION
// =============================================

export async function buildTicketTrainingContext(
  ticketId: string
): Promise<TicketTrainingContext> {
  const sb = supabase as any;

  // Run all 6 queries in parallel (5 original + OptiBay)
  const [complaintsRes, notesRes, diagRes, inspectionRes, commsRes, optibayRes] = await Promise.all([
    supabase
      .from('ticket_workflow_complaints')
      .select('text')
      .eq('ticket_id', ticketId),
    supabase
      .from('ticket_workflow_notes')
      .select('note_text')
      .eq('ticket_id', ticketId),
    supabase
      .from('ticket_workflow_diagnostics')
      .select('concern, conditions, findings, root_cause, recommendation, scan_details, dtc_list')
      .eq('ticket_id', ticketId),
    supabase
      .from('ticket_workflow_inspection_items')
      .select('label, notes')
      .eq('ticket_id', ticketId)
      .not('notes', 'is', null),
    supabase
      .from('ticket_thread_events')
      // Core-100 R4 W4 (CORE-AF): event_source too, so AI-derived text is marked as derived (never a person's words).
      .select('content, event_source')
      .eq('ticket_id', ticketId)
      .in('channel', ['sms', 'email', 'voice'])
      // V-E-2 R151-MED-1: cap to 50 most-recent events. A long-running ticket
      // can accumulate hundreds of SMS/email/voice events; the contextual
      // training scorer only needs recent communications to derive intent.
      // This bounds payload size for ticket_id-scoped event reads.
      .order('created_at', { ascending: false })
      .limit(50),
    // OptiBay: fetch session interpretations + evidence summary
    sb
      .from('optibay_sessions')
      .select(`
        symptom_text,
        system_family_id,
        optibay_session_interpretations (interpretation_text, confidence, root_cause_category),
        optibay_session_evidence (evidence_type, label, value_text),
        optibay_learning_outcomes (outcome_status, root_cause_category, confidence, technician_notes)
      `)
      .eq('ticket_id', ticketId)
      .eq('status', 'completed'),
  ]);

  // CORE-AH-INT-005 — collect through the shared helper so a failed read stays
  // a NAMED FAILURE instead of becoming an empty array.
  const complaints = collectContextSource('complaints', complaintsRes);
  const notes = collectContextSource('notes', notesRes);
  const diag = collectContextSource('diagnostics', diagRes);
  const inspection = collectContextSource('inspection', inspectionRes);
  const comms = collectContextSource('communications', commsRes);
  // The OptiBay read goes through the untyped `sb` client because it uses a
  // PostgREST embedded join the generated types do not describe. Naming the
  // row shape here is the declaration of that contract - `OptiBaySessionRow`
  // is exactly what the select list above produces - rather than an erasure.
  const optibay = collectContextSource<OptiBaySessionRow>('optibay', optibayRes);

  const { ctx, legacyState } = await assembleContext(
    ticketId,
    complaints.rows,
    notes.rows,
    diag.rows,
    inspection.rows,
    comms.rows,
    optibay.rows,
  );

  return {
    ...ctx,
    ...summariseContextStates([
      complaints.state, notes.state, diag.state,
      inspection.state, comms.state, optibay.state,
      // Present only when the legacy fallback was actually attempted.
      ...(legacyState ? [legacyState] : []),
    ]),
  };
}

// =============================================
// BATCH FUNCTION (for schedule-aware)
// =============================================

/**
 * Fetch workflow context for multiple tickets in exactly 5 queries
 * (+ optional 6th for legacy fallback).
 */
export async function buildTicketTrainingContextBatch(
  ticketIds: string[]
): Promise<Map<string, TicketTrainingContext>> {
  if (ticketIds.length === 0) return new Map();

  // BAT-A1: each `.in('ticket_id', ticketIds)` is now routed through
  // `chunkInQuery` so a 1000-ticket schedule window doesn't push a 1000-id
  // IN(...) clause into 5 sister reads. The Promise.all shape is preserved
  // — chunkInQuery itself fans out per-chunk in parallel internally, so
  // total wall-clock cost scales linearly with chunk count, not chunk-list
  // length. For typical schedule windows (≤500 tickets) the helper takes
  // its single-chunk fast path and the wire-level behavior is identical
  // to the pre-BAT-A1 direct `.in()` call.
  const [complaintsRes, notesRes, diagRes, inspectionRes, commsRes] = await Promise.all([
    chunkInQuery<string, { ticket_id: string; text: string | null }>(
      ticketIds,
      (chunk) =>
        supabase
          .from('ticket_workflow_complaints')
          .select('ticket_id, text')
          .in('ticket_id', chunk),
    ),
    chunkInQuery<string, { ticket_id: string; note_text: string | null }>(
      ticketIds,
      (chunk) =>
        supabase
          .from('ticket_workflow_notes')
          .select('ticket_id, note_text')
          .in('ticket_id', chunk),
    ),
    chunkInQuery<
      string,
      {
        ticket_id: string;
        concern: string | null;
        conditions: string | null;
        findings: string | null;
        root_cause: string | null;
        recommendation: string | null;
        scan_details: string | null;
        dtc_list: string | null;
      }
    >(ticketIds, (chunk) =>
      supabase
        .from('ticket_workflow_diagnostics')
        .select(
          'ticket_id, concern, conditions, findings, root_cause, recommendation, scan_details, dtc_list',
        )
        .in('ticket_id', chunk),
    ),
    chunkInQuery<string, { ticket_id: string; label: string | null; notes: string | null }>(
      ticketIds,
      (chunk) =>
        supabase
          .from('ticket_workflow_inspection_items')
          .select('ticket_id, label, notes')
          .in('ticket_id', chunk)
          .not('notes', 'is', null),
    ),
    chunkInQuery<string, { ticket_id: string; content: string | null; event_source?: string | null }>(
      ticketIds,
      (chunk) =>
        supabase
          .from('ticket_thread_events')
          .select('ticket_id, content, event_source')
          .in('ticket_id', chunk)
          .in('channel', ['sms', 'email', 'voice']),
    ),
  ]);

  // Group results by ticket_id
  const groupBy = <T extends { ticket_id: string }>(rows: T[]): Map<string, T[]> => {
    const map = new Map<string, T[]>();
    for (const row of rows) {
      const existing = map.get(row.ticket_id) || [];
      existing.push(row);
      map.set(row.ticket_id, existing);
    }
    return map;
  };

  // CORE-AH-INT-005 — the batch path carried the same defect. A failure here
  // is shop-wide rather than per-ticket, so every ticket in the batch inherits
  // the same unavailable source rather than each appearing to have no evidence.
  // `chunkInQuery` already returns `{ data: TRow[]; error: unknown | null }`,
  // which is exactly `collectContextSource`'s parameter shape. The `as never`
  // casts that used to sit here erased TRow, so every grouped map degenerated
  // to the `{ ticket_id: string }` constraint and the assembled rows no longer
  // matched the assembler's contract. Passing the results through untouched
  // keeps the real row types end to end.
  const bComplaints = collectContextSource('complaints', complaintsRes);
  const bNotes = collectContextSource('notes', notesRes);
  const bDiag = collectContextSource('diagnostics', diagRes);
  const bInspection = collectContextSource('inspection', inspectionRes);
  const bComms = collectContextSource('communications', commsRes);
  const batchStates = summariseContextStates([
    bComplaints.state, bNotes.state, bDiag.state, bInspection.state, bComms.state,
  ]);

  const complaintsByTicket = groupBy(bComplaints.rows);
  const notesByTicket = groupBy(bNotes.rows);
  const diagByTicket = groupBy(bDiag.rows);
  const inspectionByTicket = groupBy(bInspection.rows);
  const commsByTicket = groupBy(bComms.rows);

  const result = new Map<string, TicketTrainingContext>();

  // First pass: assemble workflow context for each ticket
  const emptyTicketIds: string[] = [];

  for (const ticketId of ticketIds) {
    const ctx = assembleContextFromGrouped(
      complaintsByTicket.get(ticketId) || [],
      notesByTicket.get(ticketId) || [],
      diagByTicket.get(ticketId) || [],
      inspectionByTicket.get(ticketId) || [],
      commsByTicket.get(ticketId) || [],
    );

    if (ctx.sources.length === 0) {
      emptyTicketIds.push(ticketId);
    }
    // CORE-AH-INT-005 — a batch read failure is shop-wide, so every ticket in
    // the batch inherits the same unavailable source rather than each one
    // appearing to have no evidence of its own.
    result.set(ticketId, { ...ctx, ...batchStates });
  }

  // Conditional 6th query: legacy fallback for tickets with zero workflow content
  if (emptyTicketIds.length > 0) {
    // BAT-A1: chunk the legacy-fallback `.in('ticket_id', emptyTicketIds)`
    // for symmetry with the 5 sister reads above. In practice
    // emptyTicketIds is a strict subset of ticketIds (only tickets that
    // returned zero workflow content), so this branch is the same size or
    // smaller than the chunked reads above — but routing it through the
    // helper means a regression on the upstream chunk size automatically
    // bounds this fallback too.
    const legacyRes = await chunkInQuery<
      string,
      { ticket_id: string; notes: string | null }
    >(emptyTicketIds, (chunk) =>
      supabase
        .from('ticket_jobs')
        .select('ticket_id, notes')
        .in('ticket_id', chunk)
        .not('notes', 'is', null),
    );
    // CORE-AH-INT-005 — the legacy read is a SIXTH source, not an escape from
    // the contract. Two failures were possible here and both are now named:
    //
    //   * this branch used to REPLACE the context wholesale, discarding the
    //     workflow read outcomes. A ticket reached it precisely BECAUSE its
    //     workflow sections were empty - and a failed read is one of the ways
    //     a section comes back empty - so the one case where the disclosure
    //     mattered most was the case that dropped it.
    //   * `legacyRes.error` was ignored, so a denied or timed-out legacy read
    //     became "this ticket has no legacy notes": failure as fact.
    const legacy = collectContextSource('legacy_notes', legacyRes);

    // The legacy source was attempted for exactly these tickets, so its state
    // joins theirs - whether or not it produced any text.
    const withLegacy = summariseContextStates([
      ...batchStates.sourceStates,
      legacy.state,
    ]);

    const legacyByTicket = groupBy(legacy.rows);
    for (const ticketId of emptyTicketIds) {
      const base = result.get(ticketId);
      if (!base) continue;

      const legacyTexts = (legacyByTicket.get(ticketId) ?? [])
        .map(r => r.notes)
        .filter(Boolean) as string[];

      if (legacyTexts.length > 0) {
        result.set(ticketId, {
          text: `[Legacy Notes]:\n${legacyTexts.join('\n')}`,
          dtcs: extractDTCs(legacyTexts.join(' ')),
          sources: ['legacy_notes'],
          ...withLegacy,
        });
      } else {
        // No legacy text. The read still happened, and if it FAILED this is
        // the only place that fact can be recorded for this ticket.
        result.set(ticketId, { ...base, ...withLegacy });
      }
    }
  }

  return result;
}

// =============================================
// INTERNAL ASSEMBLY
// =============================================

/**
 * Assemble context for a single ticket, with legacy fallback query if needed.
 */
async function assembleContext(
  ticketId: string,
  complaints: Array<{ text: string | null }>,
  notes: Array<{ note_text: string | null }>,
  diagnostics: DiagnosticRow[],
  inspections: Array<{ label: string | null; notes: string | null }>,
  comms: Array<{ content: string | null; event_source?: string | null }>,
  optibay?: OptiBaySessionRow[],
): Promise<{ ctx: TicketTrainingContext; legacyState: ContextSourceState | null }> {
  const ctx = assembleContextFromGrouped(complaints, notes, diagnostics, inspections, comms, optibay);

  // Legacy fallback: if all workflow sections returned nothing
  if (ctx.sources.length === 0) {
    // CORE-AH-INT-005 - destructuring only `data` here was the same defect the
    // five workflow reads were repaired for: a denied or timed-out legacy read
    // was indistinguishable from a ticket with no legacy notes. The outcome is
    // returned to the caller, which owns the state overlay.
    const legacyRes = await supabase
      .from('ticket_jobs')
      .select('notes')
      .eq('ticket_id', ticketId)
      .not('notes', 'is', null);

    const legacy = collectContextSource('legacy_notes', legacyRes);
    const legacyTexts = legacy.rows
      .map(r => r.notes)
      .filter(Boolean) as string[];
    if (legacyTexts.length > 0) {
      ctx.text = `[Legacy Notes]:\n${legacyTexts.join('\n')}`;
      ctx.dtcs = extractDTCs(legacyTexts.join(' '));
      ctx.sources = ['legacy_notes'];
    }

    return { ctx, legacyState: legacy.state };
  }

  // The legacy source was never attempted, so it has no state to report.
  return { ctx, legacyState: null };
}

/**
 * Shape of OptiBay session data joined from the query.
 */
interface OptiBaySessionRow {
  symptom_text: string | null;
  system_family_id: string | null;
  optibay_session_interpretations?: Array<{
    interpretation_text: string;
    confidence: string;
    root_cause_category: string | null;
  }>;
  optibay_session_evidence?: Array<{
    evidence_type: string;
    label: string | null;
    value_text: string | null;
  }>;
  optibay_learning_outcomes?: Array<{
    outcome_status: string;
    root_cause_category: string | null;
    confidence: string;
    technician_notes: string | null;
  }>;
}

/**
 * Assemble OptiBay diagnostic text from session data.
 */
function assembleOptiBayText(sessions: OptiBaySessionRow[]): string[] {
  const texts: string[] = [];
  for (const s of sessions) {
    const parts: string[] = [];
    if (s.symptom_text) parts.push(`Symptom: ${s.symptom_text}`);

    const interps = s.optibay_session_interpretations || [];
    for (const i of interps) {
      parts.push(`Interpretation (${i.confidence}): ${i.interpretation_text}`);
      if (i.root_cause_category) parts.push(`Root cause: ${i.root_cause_category}`);
    }

    const evidence = s.optibay_session_evidence || [];
    const evidenceNotes = evidence
      .filter(e => e.value_text)
      .map(e => `${e.label || e.evidence_type}: ${e.value_text}`);
    if (evidenceNotes.length) parts.push(`Evidence notes: ${evidenceNotes.join('; ')}`);

    const outcomes = s.optibay_learning_outcomes || [];
    for (const o of outcomes) {
      parts.push(`Outcome: ${o.outcome_status}`);
      if (o.technician_notes) parts.push(`Tech notes: ${o.technician_notes}`);
    }

    if (parts.length) texts.push(parts.join('\n'));
  }
  return texts;
}

/**
 * Pure assembly from pre-fetched data (no DB calls).
 * Used by both single-ticket and batch paths.
 */
function assembleContextFromGrouped(
  complaints: Array<{ text?: string | null }>,
  notes: Array<{ note_text?: string | null }>,
  diagnostics: DiagnosticRow[],
  inspections: Array<{ label?: string | null; notes?: string | null }>,
  comms: Array<{ content?: string | null; event_source?: string | null }>,
  optibay?: OptiBaySessionRow[],
): TicketTrainingContext {
  const sections: string[] = [];
  const sources: WorkflowSource[] = [];
  const allDtcs: string[] = [];

  // 1. Complaints
  const complaintTexts = complaints
    .map(c => c.text)
    .filter(Boolean) as string[];
  if (complaintTexts.length > 0) {
    sections.push(`[Complaints]:\n${complaintTexts.join('\n')}`);
    sources.push('workflow_complaints');
  }

  // 2. Diagnostics (before notes — DTCs are extracted here)
  const diagTexts: string[] = [];
  for (const row of diagnostics) {
    const text = assembleDiagnosticText(row);
    if (text) diagTexts.push(text);
    // Parse DTCs from dtc_list column
    allDtcs.push(...parseDtcList(row.dtc_list));
  }
  if (diagTexts.length > 0) {
    sections.push(`[Diagnostics]:\n${diagTexts.join('\n')}`);
    sources.push('workflow_diagnostics');
  }

  // 3. Notes
  const noteTexts = notes
    .map(n => n.note_text)
    .filter(Boolean) as string[];
  if (noteTexts.length > 0) {
    sections.push(`[Notes]:\n${noteTexts.join('\n')}`);
    sources.push('workflow_notes');
  }

  // 4. Inspection items
  const inspTexts = inspections
    .filter(i => i.notes)
    .map(i => `${i.label || 'Item'}: ${i.notes}`);
  if (inspTexts.length > 0) {
    sections.push(`[Inspection]:\n${inspTexts.join('\n')}`);
    sources.push('workflow_inspection');
  }

  // 5. Communications
  // Core-100 R4 W4 (CORE-AF "AI is marked as derived"): an AI-written timeline entry is not the customer's or a
  // person's words; it stays in the context, labelled as derived.
  const commTexts = comms
    .map(c => (c.content && c.event_source === 'ai' ? `${AI_DERIVED_COMMS_PREFIX} ${c.content}` : c.content))
    .filter(Boolean) as string[];
  if (commTexts.length > 0) {
    sections.push(`[Communications]:\n${commTexts.join('\n')}`);
    sources.push('workflow_comms');
  }

  // 6. OptiBay diagnostic sessions
  if (optibay && optibay.length > 0) {
    const optibayTexts = assembleOptiBayText(optibay);
    if (optibayTexts.length > 0) {
      sections.push(`[OptiBay Diagnostics]:\n${optibayTexts.join('\n---\n')}`);
      sources.push('optibay_diagnostics');
    }
  }

  const combinedText = sections.join('\n\n');

  // Extract DTCs from full text as fallback (catches codes not in dtc_list)
  const textDtcs = extractDTCs(combinedText);
  const allUniqueDtcs = [...new Set([...allDtcs, ...textDtcs])];

  // CORE-AH-INT-005 — this helper only assembles ROWS it was handed; the
  // caller owns the read outcomes and overlays them. Defaulting to "nothing
  // attempted" here keeps the shape total without inventing a success.
  return {
    text: combinedText,
    dtcs: allUniqueDtcs,
    sources,
    ...summariseContextStates([]),
  };
}
